// installedPositions.ts — Compute each panel's erected center position in plan coordinates.
// The cast yard and building footprint share the same coordinate space, so cast positions
// and installed positions can be connected directly with arrows.

import { computeFootprint } from './buildingFootprint';
import type { Elevation, PanelData } from '../types/panelvar';

export interface InstalledPosition {
  x:         number;   // plan X (inches, right = positive)
  y:         number;   // plan Y (inches, down = positive)
  wallAngle: number;   // direction along wall in radians (from buildingFootprint wall.angle)
}

/**
 * Walk each footprint wall and compute the center point of every panel
 * as it sits erected on the wall, in plan coordinates.
 *
 * Along-wall unit vector: (cos(angle), -sin(angle)) — matches buildingFootprint.ts
 * where endX = startX + cos(angle)*width and endY = startY - sin(angle)*width.
 */
export function computePanelInstalledPositions(
  elevations: Elevation[],
  allPanels: PanelData[],
  jointWidth: number,
): Map<string, InstalledPosition> {
  const result = new Map<string, InstalledPosition>();
  const fp = computeFootprint(elevations, allPanels, jointWidth);

  for (const wall of fp.walls) {
    const cosA = Math.cos(wall.angle);
    const sinA = Math.sin(wall.angle);
    let offset = wall.leftEndJoint;

    for (let i = 0; i < wall.panels.length; i++) {
      const panel = wall.panels[i];
      offset += panel.mpvar.width / 2;
      result.set(panel.id, {
        x:         wall.startX + offset * cosA,
        y:         wall.startY - offset * sinA,
        wallAngle: wall.angle,
      });
      offset += panel.mpvar.width / 2;
      if (i < wall.panels.length - 1) offset += jointWidth;
    }
  }

  return result;
}
